import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, Grid } from '@mui/material';
import { useMachine } from '@xstate/react';
import { useEffect, type ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { VisitFinishSetting } from '../../../api/types/visits';
import {
  createVisitMachine,
  CreateVisitStates,
  CreateVisitTitle,
} from '../../../utils/machine/create-visit-machine';
import { Confirmation } from '../../molecules/create-visit/confirmation';
import { DatesInfo } from '../../molecules/create-visit/dates-info';
import { DestinationInfo } from '../../molecules/create-visit/destination-info';
import { HostInfo } from '../../molecules/create-visit/host-info';
import { VisitorAdditionalInformation } from '../../molecules/create-visit/visitor-additional-information';
import { VisitorInfo } from '../../molecules/create-visit/visitor-info';

import {
  CreateVisitFormValues,
  defaultValues,
  useCreateVisitValidationSchema,
} from './schema/create-visit-validation-schema';

import { Stepper } from '@/components/molecules/stepper/stepper';
import { useOrganizationDetails } from '@/features/settings/hooks/query/use-organization-details';
import { useScrollToTopOnChange } from '@/hooks/use-scroll-to-top-on-change';

export const CreateVisit = () => {
  const { t } = useTranslation(['createVisit']);

  const { visitSchema } = useCreateVisitValidationSchema();

  const methods = useForm<CreateVisitFormValues>({
    defaultValues,
    resolver: zodResolver(visitSchema),
    mode: 'onBlur',
  });

  const { setValue } = methods;

  const { data: organizationDetailsData } = useOrganizationDetails();

  const [current, send] = useMachine(createVisitMachine, {
    input: {
      step: '1',
      content: CreateVisitTitle.HOST_INFO,
    },
  });

  const { value, context } = current;

  useScrollToTopOnChange(value);

  const stepTitles = [
    CreateVisitTitle.HOST_INFO,
    CreateVisitTitle.DESTINATION_INFO,
    CreateVisitTitle.VISITOR_DETAILS,
    CreateVisitTitle.DATES_INFO,
    CreateVisitTitle.ADDITIONAL_INFORMATION,
    CreateVisitTitle.CONFIRMATION,
  ];

  const steps = stepTitles.map((title, index) => ({
    step: String(index + 1),
    title: t(title),
  }));

  useEffect(() => {
    if (organizationDetailsData) {
      setValue(
        'visitFinishSetting',
        organizationDetailsData?.visitFinishSetting ?? VisitFinishSetting.TIME_ENDS,
        {
          shouldValidate: true,
          shouldDirty: true,
        },
      );
    }
  }, [organizationDetailsData]);

  const renderContent = (): ReactNode => {
    switch (value) {
      case CreateVisitStates.VISITOR_DETAILS:
        return <VisitorInfo context={context} send={send} />;
      case CreateVisitStates.DATES_INFO:
        return <DatesInfo context={context} send={send} />;
      case CreateVisitStates.HOST_INFO:
        return <HostInfo context={context} send={send} />;
      case CreateVisitStates.DESTINATION_INFO:
        return <DestinationInfo context={context} send={send} />;
      case CreateVisitStates.ADDITIONAL_INFORMATION:
        return <VisitorAdditionalInformation context={context} send={send} />;
      case CreateVisitStates.CONFIRMATION:
        return <Confirmation context={context} send={send} />;
    }
  };

  return (
    <Card variant="outlined" sx={{ display: 'flex', flexGrow: 1 }}>
      <FormProvider {...methods}>
        <CardContent sx={{ display: 'flex', flexGrow: 1 }}>
          <Grid container spacing={3} sx={{ flexGrow: 1 }}>
            <Grid item md={2.5} xs={12}>
              <Stepper orientation="vertical" steps={steps} context={context} />
            </Grid>
            <Grid item md={9.5} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Grid
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1,
                  '& > *': {
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                  },
                  '& form': {
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                  },
                }}>
                {renderContent()}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </FormProvider>
    </Card>
  );
};
